import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Box,
  Chip,
  Typography,
  alpha,
} from "@mui/material";
import {Save, X} from "lucide-react";
import {
  useSetRoleMutation,
  useSetUserDefaultApprovalFlowMutation,
  useGetApprovalFlowsQuery,
  UserAdminFieldsFragment
} from "../../../graphql/graphql";
import {ROLES, roleColors} from "./constants";
import {z} from "zod";
import {useForm, Controller} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import {Link} from "react-router-dom";

const schema = z.object({
  roleIds: z.array(z.string()).min(1, "ロールは1つ以上選択してください"),
  defaultApprovalFlowId: z.string().nullable(),
});

type FormValues = z.infer<typeof schema>;

interface UserEditDialogProps {
  user: {
    id: string;
    name: string;
    email: string;
    roles: string[];
    defaultApprovalFlowId?: string | null;
  };
  onClose: () => void;
  onComplete: (user: UserAdminFieldsFragment) => void;
}

export const UserEditDialog = ({user, onClose, onComplete}: UserEditDialogProps) => {
  const {mutateAsync: setRole} = useSetRoleMutation();
  const {mutateAsync: setUserDefaultApprovalFlow} = useSetUserDefaultApprovalFlowMutation();
  const {data: approvalFlowsData} = useGetApprovalFlowsQuery();
  
  const {
    control,
    handleSubmit,
    formState: {errors, isSubmitting},
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      roleIds: user.roles,
      defaultApprovalFlowId: user.defaultApprovalFlowId || null,
    },
  });
  
  const onSubmit = async (data: FormValues) => {
    try {
      const result = await setRole({
        userId: user.id,
        roleIds: data.roleIds,
      });
      
      await setUserDefaultApprovalFlow({
        userId: user.id,
        id: data.defaultApprovalFlowId,
      });
      
      if (result?.setRole) {
        onComplete({
          ...result.setRole,
          defaultApprovalFlowId: data.defaultApprovalFlowId,
          defaultApprovalFlow: approvalFlowsData?.getApprovalFlows.find(
            (flow) => flow.id === data.defaultApprovalFlowId
          ),
        });
      }
    } catch (error) {
      console.error("Failed to update user:", error);
      toast.error("ユーザー情報の更新に失敗しました");
    }
  };
  
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>ユーザー編集: {user.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{mt: 2}}>
            <FormControl fullWidth error={!!errors.roleIds}>
              <Typography
                component="label"
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                ロール
              </Typography>
              <Controller
                name="roleIds"
                control={control}
                render={({field}) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {ROLES.map((role) => {
                      const isSelected = field.value.includes(role.id);
                      const colors = roleColors[role.id];
                      return (
                        <Chip
                          key={role.id}
                          label={role.label}
                          onClick={() => {
                            if (isSelected) {
                              field.onChange(field.value.filter(id => id !== role.id));
                            } else {
                              field.onChange([...field.value, role.id]);
                            }
                          }}
                          sx={{
                            color: isSelected ? colors.color : 'text.secondary',
                            backgroundColor: isSelected ? colors.backgroundColor : 'transparent',
                            border: '1px solid',
                            borderColor: isSelected ? colors.color : 'divider',
                            fontWeight: isSelected ? 600 : 400,
                            '&:hover': {
                              backgroundColor: isSelected 
                                ? alpha(colors.backgroundColor, 0.8)
                                : alpha(colors.backgroundColor, 0.1),
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              />
              {errors.roleIds && (
                <FormHelperText error>{errors.roleIds.message}</FormHelperText>
              )}
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>デフォルト承認フロー</InputLabel>
              <Controller
                name="defaultApprovalFlowId"
                control={control}
                render={({field}) => (
                  <Select
                    {...field}
                    label="デフォルト承認フロー"
                  >
                    <MenuItem value="">
                      <em>設定しない</em>
                    </MenuItem>
                    {approvalFlowsData?.getApprovalFlows
                      .filter(flow => !flow.isDeleted)
                      .map((flow) => (
                        <MenuItem key={flow.id} value={flow.id}>
                          {flow.name}
                        </MenuItem>
                      ))}
                  </Select>
                )}
              />
              <Link 
                to={"/admin/approvalFlow"}
                style={{ textDecoration: 'none' }}
              >
                <Typography
                  sx={{
                    mt: 1,
                    color: 'primary.main',
                    fontSize: '0.875rem',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  承認フローを設定する
                </Typography>
              </Link>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} startIcon={<X size={20}/>}>
            キャンセル
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<Save size={20}/>}
            disabled={isSubmitting}
          >
            保存
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
